// src/hooks/use-asset-settings.ts
import { useCallback } from 'react';

export interface AssetSettings {
	baseMapId: string;
	undergroundEnabled: boolean;
	alpha: number;
	layerVisibility: Record<string, boolean>; // Keyed by layer ID (e.g., 'asset-123', 'sensors-layer')
	baseMapEnabled: boolean;
}

const DEFAULT_SETTINGS: AssetSettings = {
	baseMapId: 'cesium-world',
	undergroundEnabled: false,
	alpha: 0.5,
	layerVisibility: {},
	baseMapEnabled: true,
};

export const useAssetSettings = (assetId: string | undefined) => {
	const getStorageKey = () => `map_settings_${assetId || 'default'}`;

	const getSettings = useCallback((): AssetSettings => {
		if (!assetId) return DEFAULT_SETTINGS;
		try {
			const saved = localStorage.getItem(getStorageKey());
			return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
		} catch (e) {
			console.warn('Failed to parse map settings', e);
			return DEFAULT_SETTINGS;
		}
	}, [assetId]);

	const updateSetting = useCallback(
		(key: keyof AssetSettings, value: any) => {
			if (!assetId) return;
			const current = getSettings();

			// If updating visibility, merge with existing map
			let newValue = value;
			if (key === 'layerVisibility') {
				newValue = { ...current.layerVisibility, ...value };
			}

			const updated = { ...current, [key]: newValue };
			localStorage.setItem(getStorageKey(), JSON.stringify(updated));
		},
		[assetId, getSettings]
	);

	const getLayerVisibility = useCallback(
		(layerId: string, defaultValue: boolean = true) => {
			const settings = getSettings();
			// If the key exists, return it, otherwise return default
			return settings.layerVisibility.hasOwnProperty(layerId)
				? settings.layerVisibility[layerId]
				: defaultValue;
		},
		[getSettings]
	);

	return {
		getSettings,
		updateSetting,
		getLayerVisibility,
	};
};
